import { useState, useEffect } from 'react'
import { subscriptionsAPI, customersAPI, appsAPI, appPlansAPI } from '../../services/api'

import { logger } from '@/utils/logger';
export default function SubscriptionsManagement() {
  const [subscriptions, setSubscriptions] = useState([])
  const [customers, setCustomers] = useState([])
  const [apps, setApps] = useState([])
  const [appPlans, setAppPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingSubscription, setEditingSubscription] = useState(null)
  const [formData, setFormData] = useState({
    customer_id: '',
    app_id: '',
    plan_id: '',
    plan: 'MONTHLY',
    status: 'ACTIVE',
    end_date: '',
    is_active: true,
    auto_renew: true
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [subsResponse, custResponse, appsResponse] = await Promise.all([
        subscriptionsAPI.getAllAdmin(),
        customersAPI.getAll(),
        appsAPI.getAll()
      ])
      setSubscriptions(subsResponse.data)
      setCustomers(custResponse.data)
      setApps(appsResponse.data)
    } catch (err) {
      setError('Fehler beim Laden der Daten')
      logger.error('Error loading data:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadAppPlans = async (appId) => {
    if (!appId) {
      setAppPlans([])
      return
    }
    try {
      const response = await appPlansAPI.getAll(appId)
      setAppPlans(response.data)
    } catch (err) {
      logger.error('Error loading app plans:', err)
      setAppPlans([])
    }
  }

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId)
    return customer ? (customer.company_name || customer.username) : 'Unbekannt'
  }

  const getAppName = (appId) => {
    const app = apps.find(a => a.id === appId)
    return app ? app.name : 'Unbekannt'
  }

  const handleAppChange = async (appId) => {
    setFormData({ ...formData, app_id: appId, plan_id: '' })
    await loadAppPlans(appId)
  }

  const handleOpenModal = async (subscription = null) => {
    if (subscription) {
      setEditingSubscription(subscription)
      setFormData({
        customer_id: subscription.customer_id,
        app_id: subscription.app_id,
        plan_id: subscription.plan_id ? String(subscription.plan_id) : '',
        plan: subscription.plan,
        status: subscription.status,
        end_date: subscription.end_date ? new Date(subscription.end_date).toISOString().split('T')[0] : '',
        is_active: subscription.is_active,
        auto_renew: subscription.auto_renew
      })
      if (subscription.app_id) {
        await loadAppPlans(subscription.app_id)
      }
    } else {
      setEditingSubscription(null)
      setFormData({
        customer_id: '',
        app_id: '',
        plan_id: '',
        plan: 'MONTHLY',
        status: 'ACTIVE',
        end_date: '',
        is_active: true,
        auto_renew: true
      })
      setAppPlans([])
    }
    setShowModal(true)
    setError('')
    setSuccess('')
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingSubscription(null)
    setAppPlans([])
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      const submitData = {
        ...formData,
        customer_id: parseInt(formData.customer_id),
        app_id: parseInt(formData.app_id),
        plan_id: formData.plan_id ? parseInt(formData.plan_id) : null,
        end_date: formData.end_date || null
      }

      if (editingSubscription) {
        await subscriptionsAPI.update(editingSubscription.id, submitData)
        setSuccess('Abonnement erfolgreich aktualisiert')
      } else {
        await subscriptionsAPI.create(submitData)
        setSuccess('Abonnement erfolgreich erstellt')
      }

      await loadData()
      handleCloseModal()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern des Abonnements')
      logger.error('Error saving subscription:', err)
    }
  }

  const handleDelete = async (subscription) => {
    const customerName = getCustomerName(subscription.customer_id)
    const appName = getAppName(subscription.app_id)

    if (!confirm(`Möchten Sie das Abonnement von "${customerName}" für "${appName}" wirklich löschen?`)) {
      return
    }

    try {
      await subscriptionsAPI.delete(subscription.id)
      setSuccess('Abonnement erfolgreich gelöscht')
      await loadData()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Löschen des Abonnements')
      logger.error('Error deleting subscription:', err)
      setTimeout(() => setError(''), 3000)
    }
  }

  const statusTagClass = {
    ACTIVE: 'wc-tag-accent',
    TRIAL: 'wc-tag-outline',
    CANCELLED: 'wc-tag-neutral',
    CANCELLED_PENDING: 'wc-tag-neutral',
    EXPIRED: 'wc-tag-neutral',
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'Aktiv'
      case 'TRIAL':
        return 'Testversion'
      case 'CANCELLED':
        return 'Gekündigt'
      case 'CANCELLED_PENDING':
        return 'Kündigung beantragt'
      case 'EXPIRED':
        return 'Abgelaufen'
      default:
        return status
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>Abonnement-Verwaltung</h1>
        <button onClick={() => handleOpenModal()} className="wc-btn wc-btn-primary">
          + Neues Abonnement
        </button>
      </div>

      {error && <div className="wc-alert wc-alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="wc-alert wc-alert-success" style={{ marginBottom: 16 }}>{success}</div>}

      <div className="wc-card wc-elev-sm" style={{ padding: 0 }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="wc-table">
            <thead>
              <tr>
                <th>Kunde</th>
                <th>App</th>
                <th>Plan</th>
                <th>Abo-Typ</th>
                <th>Status</th>
                <th>Startdatum</th>
                <th>Enddatum</th>
                <th>Auto-Verlängerung</th>
                <th style={{ textAlign: 'right' }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '48px 0' }} className="wc-muted">
                    Keine Abonnements vorhanden
                  </td>
                </tr>
              ) : (
                subscriptions.map((subscription) => (
                  <tr key={subscription.id}>
                    <td>{getCustomerName(subscription.customer_id)}</td>
                    <td>{getAppName(subscription.app_id)}</td>
                    <td>{subscription.app_plan ? subscription.app_plan.name : '-'}</td>
                    <td>
                      <span className="wc-tag wc-tag-outline">
                        {subscription.plan === 'MONTHLY' ? 'Monatlich' : 'Jährlich'}
                      </span>
                    </td>
                    <td>
                      <span className={`wc-tag ${statusTagClass[subscription.status] || 'wc-tag-neutral'}`}>
                        {getStatusLabel(subscription.status)}
                      </span>
                    </td>
                    <td className="wc-muted">{new Date(subscription.start_date).toLocaleDateString('de-DE')}</td>
                    <td className="wc-muted">
                      {subscription.end_date ? new Date(subscription.end_date).toLocaleDateString('de-DE') : '-'}
                    </td>
                    <td className="wc-muted">{subscription.auto_renew ? '✓ Ja' : '✗ Nein'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={() => handleOpenModal(subscription)} className="wc-btn wc-btn-ghost" style={{ padding: '4px 8px' }}>
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDelete(subscription)}
                        className="wc-btn wc-btn-ghost"
                        style={{ padding: '4px 8px', color: '#fca5a5' }}
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="wc-modal-overlay" onClick={handleCloseModal}>
          <div className="wc-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="wc-card-title" style={{ margin: 0 }}>
              {editingSubscription ? 'Abonnement bearbeiten' : 'Neues Abonnement'}
            </h3>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                <div className="wc-field">
                  <label>Kunde *</label>
                  <select
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                    className="wc-input"
                    required
                    disabled={!!editingSubscription}
                  >
                    <option value="">Bitte wählen...</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.company_name || customer.username} ({customer.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="wc-field">
                  <label>App *</label>
                  <select
                    value={formData.app_id}
                    onChange={(e) => handleAppChange(e.target.value)}
                    className="wc-input"
                    required
                    disabled={!!editingSubscription}
                  >
                    <option value="">Bitte wählen...</option>
                    {apps.map(app => (
                      <option key={app.id} value={app.id}>
                        {app.name}
                      </option>
                    ))}
                  </select>
                </div>

                {appPlans.length > 0 && (
                  <div className="wc-field">
                    <label>Plan *</label>
                    <select
                      value={formData.plan_id}
                      onChange={(e) => setFormData({ ...formData, plan_id: e.target.value })}
                      className="wc-input"
                      required
                    >
                      <option value="">Bitte wählen...</option>
                      {appPlans.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} - {plan.price_monthly}€/Monat
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="wc-field">
                  <label>Abrechnungszyklus *</label>
                  <select
                    value={formData.plan}
                    onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                    className="wc-input"
                    required
                  >
                    <option value="MONTHLY">Monatlich</option>
                    <option value="YEARLY">Jährlich</option>
                  </select>
                </div>

                <div className="wc-field">
                  <label>Status *</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="wc-input"
                    required
                  >
                    <option value="ACTIVE">Aktiv</option>
                    <option value="TRIAL">Testversion</option>
                    <option value="CANCELLED_PENDING">Kündigung beantragt</option>
                    <option value="CANCELLED">Gekündigt</option>
                    <option value="EXPIRED">Abgelaufen</option>
                  </select>
                </div>

                <div className="wc-field" style={{ gridColumn: 'span 2' }}>
                  <label>Enddatum (optional)</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="wc-input"
                  />
                </div>

                <div style={{ gridColumn: 'span 2', display: 'flex', gap: 24 }}>
                  <label className="wc-check">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    Aktiv
                  </label>

                  <label className="wc-check">
                    <input
                      type="checkbox"
                      checked={formData.auto_renew}
                      onChange={(e) => setFormData({ ...formData, auto_renew: e.target.checked })}
                    />
                    Automatische Verlängerung
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 4 }}>
                <button type="button" onClick={handleCloseModal} className="wc-btn wc-btn-secondary">
                  Abbrechen
                </button>
                <button type="submit" className="wc-btn wc-btn-primary">
                  {editingSubscription ? 'Speichern' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
